/**
 * Deterministic India-first spending insights (no AI).
 */
import { detectRecurring } from './categorization';
import { formatINR } from './leakBuckets';
import { pickSnark } from '../data/snark';

function parseAmount(str) {
    if (typeof str === 'number') return Math.abs(str);
    if (!str) return 0;
    return Math.abs(parseFloat(String(str).replace(/[^0-9.-]/g, '')) || 0);
}

function isDebit(txn) {
    return txn.type && String(txn.type).toUpperCase() === 'DEBIT';
}

function partyText(txn) {
    return `${txn.party || ''} ${txn.raw_merchant || ''} ${txn.merchant_key || ''}`.toLowerCase();
}

function extractHour(txn) {
    const raw = txn.date_time || '';
    // Common formats: "DD/MM/YYYY HH:MM", "YYYY-MM-DD HH:MM:SS", ISO
    const m = raw.match(/(\d{1,2}):(\d{2})/);
    if (!m) return null;
    let hour = parseInt(m[1], 10);
    const ampm = raw.match(/\b(am|pm)\b/i);
    if (ampm) {
        const isPM = ampm[1].toLowerCase() === 'pm';
        if (isPM && hour < 12) hour += 12;
        if (!isPM && hour === 12) hour = 0;
    }
    return hour;
}

const QCOMMERCE = ['blinkit', 'zepto', 'instamart', 'bbnow', 'bb now'];
const FOOD_APPS = ['swiggy', 'zomato'];
const FOOD_OR_COMMERCE = [...FOOD_APPS, ...QCOMMERCE, 'dominos', 'mcdonald', 'kfc'];

const UNNAMED_VPA = /(?:paytm[-\s]?\d|q\d{4,}@|[\w.-]*\d{3,}@(?:ybl|okaxis|oksbi|okhdfcbank|paytm|upi))/i;

/**
 * @returns {Array<{ id, severity, title, body, score }>} sorted by score desc
 */
export function generateInsights(transactions) {
    const debits = (transactions || []).filter(t => isDebit(t) && parseAmount(t.amount) > 0);
    if (debits.length === 0) return [];

    const insights = [];

    // 1. Chai & Tap — under ₹100
    const micro = debits.filter(t => parseAmount(t.amount) < 100);
    if (micro.length >= 5) {
        const total = micro.reduce((s, t) => s + parseAmount(t.amount), 0);
        insights.push({
            id: 'chai_tap',
            severity: 'high',
            title: 'Micro-taps',
            body: `${micro.length} under ₹100 · ${formatINR(total)} total`,
            score: Math.min(100, micro.length * 2 + total / 50),
            meta: { count: micro.length, total },
        });
    }

    // 2. Quick Commerce frequency
    const qc = debits.filter(t => QCOMMERCE.some(k => partyText(t).includes(k)));
    if (qc.length >= 3) {
        const total = qc.reduce((s, t) => s + parseAmount(t.amount), 0);
        const feeEstimate = Math.round(qc.length * 50); // rough convenience fee
        const days = 30;
        const everyN = Math.max(1, Math.round(days / qc.length));
        insights.push({
            id: 'qcommerce',
            severity: 'high',
            title: 'Quick commerce',
            body: `${qc.length} orders · ${formatINR(total)} · ~every ${everyN} days`,
            score: Math.min(100, qc.length * 6 + total / 80),
            meta: { count: qc.length, total, feeEstimate },
        });
    }

    // 3. Late Night Impulse (22:00–03:30)
    const foodNight = debits.filter(t => {
        const h = extractHour(t);
        if (h === null) return false;
        const late = h >= 22 || h < 4;
        if (!late) return false;
        return FOOD_OR_COMMERCE.some(k => partyText(t).includes(k)) || t.category === 'Food & Drinks';
    });
    const foodAll = debits.filter(t =>
        FOOD_OR_COMMERCE.some(k => partyText(t).includes(k)) || t.category === 'Food & Drinks'
    );
    if (foodNight.length >= 2 && foodAll.length >= 4) {
        const pct = Math.round((foodNight.length / foodAll.length) * 100);
        const nightSpend = foodNight.reduce((s, t) => s + parseAmount(t.amount), 0);
        insights.push({
            id: 'late_night',
            severity: 'medium',
            title: 'Late-night food',
            body: `${pct}% after 10 PM · ${formatINR(nightSpend)}`,
            score: pct * 0.9,
            meta: { pct, nightSpend, count: foodNight.length },
        });
    }

    // 4. Unnamed merchant / VPA cluster
    const unnamed = debits.filter(t => {
        const p = partyText(t);
        const amount = parseAmount(t.amount);
        if (amount >= 50) return false;
        return UNNAMED_VPA.test(p) || /@ybl|@okaxis|@oksbi|paytm-\d/i.test(p);
    });
    if (unnamed.length >= 5) {
        const total = unnamed.reduce((s, t) => s + parseAmount(t.amount), 0);
        insights.push({
            id: 'unnamed_vpa',
            severity: 'medium',
            title: 'Local vendors',
            body: `${unnamed.length} small taps · ${formatINR(total)}`,
            score: Math.min(90, unnamed.length * 3 + total / 40),
            meta: { count: unnamed.length, total },
        });
    }

    // 5. Subscription phantom
    const recurring = detectRecurring(debits);
    const known = recurring.filter(r => r.isKnownSubscription || r.type === 'subscription');
    const subTotal = known.reduce((s, r) => s + (r.avgAmount || r.totalSpent || 0), 0);
    if (known.length >= 1 && subTotal >= 200) {
        insights.push({
            id: 'subscriptions',
            severity: 'medium',
            title: 'Subscriptions',
            body: `${known.length} recurring · ~${formatINR(subTotal)}/mo`,
            score: Math.min(85, known.length * 15 + subTotal / 100),
            meta: { count: known.length, total: subTotal },
        });
    }

    // 6. P2P vs merchant
    const p2p = debits.filter(t =>
        t.category === 'Family & Friends'
        || (t.subcategory && /friend|family|person|p2p|transfer/i.test(t.subcategory))
    );
    const merchantDebits = debits.filter(t => !p2p.includes(t));
    if (debits.length >= 8 && p2p.length >= 2 && merchantDebits.length >= 2) {
        const p2pTotal = p2p.reduce((s, t) => s + parseAmount(t.amount), 0);
        const shopTotal = merchantDebits.reduce((s, t) => s + parseAmount(t.amount), 0);
        insights.push({
            id: 'p2p_split',
            severity: 'low',
            title: 'People vs shops',
            body: `${formatINR(p2pTotal)} to people · ${formatINR(shopTotal)} to shops`,
            score: Math.min(70, (p2pTotal + shopTotal) / 200),
            meta: { p2pTotal, shopTotal, p2pCount: p2p.length },
        });
    }

    // 7. Review queue — uncategorized + soft guesses
    const needsTag = debits.filter(t =>
        !t.category
        || t.category === 'Uncategorized'
        || (Number(t.confidence) === 0 && t.category_source === 'none')
    );
    const guesses = debits.filter(t => {
        const src = t.category_source;
        if (src === 'user' || src === 'manual') return false;
        if (!t.category || t.category === 'Uncategorized') return false;
        if (src === 'fuzzy' || src === 'amount_heuristic' || src === 'registry_weak') return true;
        const conf = Number(t.confidence);
        return Number.isFinite(conf) && conf > 0 && conf < 0.85;
    });
    const reviewTotal = needsTag.length + guesses.length;
    if (reviewTotal >= 3) {
        insights.push({
            id: 'needs_tag',
            severity: 'high',
            title: 'Double-check',
            body: guesses.length
                ? `${reviewTotal} to review · ${guesses.length} soft guesses`
                : `${needsTag.length} payments · fix once, remembered next time`,
            score: Math.min(95, reviewTotal * 8),
            meta: { count: reviewTotal, guesses: guesses.length },
            action: { tab: 'activity', category: '__review__' },
        });
    }

    // 8. Top remembered merchants this month
    const remembered = debits.filter(t => t.category_source === 'user' || t.category_source === 'manual');
    if (remembered.length >= 3) {
        const byKey = {};
        for (const t of remembered) {
            const key = t.merchant_key || t.party || 'unknown';
            if (!byKey[key]) byKey[key] = { key, count: 0, total: 0, category: t.category };
            byKey[key].count += 1;
            byKey[key].total += parseAmount(t.amount);
        }
        const top = Object.values(byKey).sort((a, b) => b.total - a.total)[0];
        if (top) {
            insights.push({
                id: 'remembered_top',
                severity: 'low',
                title: 'You taught this',
                body: `${top.key} · ${top.count}× · ${formatINR(top.total)} (${top.category})`,
                score: Math.min(60, remembered.length * 3),
                meta: { merchant: top.key, count: top.count, total: top.total },
            });
        }
    }

    return insights.sort((a, b) => b.score - a.score);
}

/**
 * Pick the single strongest insight for Home / Reality Check.
 */
export function getTopInsight(transactions) {
    const list = generateInsights(transactions);
    return list[0] || null;
}

function avgAmount(debits) {
    if (!debits.length) return 0;
    return debits.reduce((s, t) => s + parseAmount(t.amount), 0) / debits.length;
}

function countParty(debits, keys) {
    return debits.filter(t => keys.some(k => partyText(t).includes(k))).length;
}

/**
 * Persona + share stats for viral card.
 * Priority ladder — first match wins (most specific / loudest behaviour).
 */
export function getUpiPersona(transactions) {
    const debits = (transactions || []).filter(t => isDebit(t) && parseAmount(t.amount) > 0);
    const totalSpent = debits.reduce((s, t) => s + parseAmount(t.amount), 0);
    const micro = debits.filter(t => parseAmount(t.amount) < 100);
    const mid = debits.filter(t => {
        const a = parseAmount(t.amount);
        return a >= 100 && a < 500;
    });

    const foodCounts = {};
    for (const t of debits) {
        const p = partyText(t);
        for (const app of ['Swiggy', 'Zomato', 'Blinkit', 'Zepto', 'Instamart']) {
            if (p.includes(app.toLowerCase())) {
                foodCounts[app] = (foodCounts[app] || 0) + 1;
            }
        }
    }
    const topFood = Object.entries(foodCounts).sort((a, b) => b[1] - a[1])[0] || null;

    const qcCount = countParty(debits, QCOMMERCE);
    const foodCount = countParty(debits, FOOD_APPS);
    const cabCount = countParty(debits, ['uber', 'ola', 'rapido', 'namma yatri', 'blusmart']);
    const fuelCount = countParty(debits, ['petrol', 'diesel', 'hpcl', 'iocl', 'bpcl', 'nayara', 'shell']);
    const fashionCount = countParty(debits, ['myntra', 'ajio', 'nykaa', 'meesho', 'flipkart', 'amazon']);
    const investCount = countParty(debits, ['groww', 'zerodha', 'upstox', 'kuvera', 'coin', 'mf utility', 'nps']);
    const rentLike = debits.filter(t => {
        const a = parseAmount(t.amount);
        const p = partyText(t);
        return a >= 8000 && (/rent|landlord|house|pg |paying guest|broker/i.test(p) || t.category === 'Housing');
    }).length;
    const p2p = debits.filter(t =>
        t.category === 'Family & Friends'
        || (t.subcategory && /friend|family|person|p2p|transfer/i.test(t.subcategory))
    );
    const lateFood = debits.filter(t => {
        const h = extractHour(t);
        if (h === null) return false;
        if (!(h >= 22 || h < 4)) return false;
        return FOOD_OR_COMMERCE.some(k => partyText(t).includes(k)) || t.category === 'Food & Drinks';
    });
    const unnamed = debits.filter(t => {
        const p = partyText(t);
        const amount = parseAmount(t.amount);
        if (amount >= 80) return false;
        return UNNAMED_VPA.test(p) || /@ybl|@okaxis|@oksbi|paytm-\d/i.test(p);
    });
    const recurring = detectRecurring(debits);
    const knownSubs = recurring.filter(r => r.isKnownSubscription || r.type === 'subscription');
    const avg = avgAmount(debits);
    const bigTicket = debits.filter(t => parseAmount(t.amount) >= 5000).length;

    let persona = 'The Careful Tapper';
    let personaTags = ['general', 'share'];

    // Priority ladder (first match wins)
    if (qcCount >= 12) {
        persona = 'The 10-Minute Cart';
        personaTags = ['qcommerce', 'share'];
    } else if (qcCount >= 8) {
        persona = 'The Convenience Addict';
        personaTags = ['qcommerce', 'share'];
    } else if (lateFood.length >= 6) {
        persona = 'The Midnight Menu Scroller';
        personaTags = ['late_night', 'food', 'share'];
    } else if (foodCount >= 18) {
        persona = 'The Kitchen Avoider';
        personaTags = ['food', 'share'];
    } else if (foodCount >= 12) {
        persona = 'The Delivery Devotee';
        personaTags = ['food', 'share'];
    } else if (micro.length >= 55) {
        persona = 'The Chai Ledger';
        personaTags = ['micro', 'share'];
    } else if (micro.length >= 40) {
        persona = 'The Micro-Leak Legend';
        personaTags = ['micro', 'share'];
    } else if (unnamed.length >= 20) {
        persona = 'The Local QR Regular';
        personaTags = ['micro', 'share'];
    } else if (p2p.length >= 15 && p2p.length >= debits.length * 0.35) {
        persona = 'The Send-Na Specialist';
        personaTags = ['p2p', 'share'];
    } else if (knownSubs.length >= 4) {
        persona = 'The Auto-Pay Roommate';
        personaTags = ['subscriptions', 'share'];
    } else if (cabCount >= 10) {
        persona = 'The Surge Negotiator';
        personaTags = ['metro', 'general', 'share'];
    } else if (fashionCount >= 8) {
        persona = 'The Cart Abandoner (Failed)';
        personaTags = ['festivity', 'general', 'share'];
    } else if (investCount >= 4) {
        persona = 'The SIP Starter (Maybe)';
        personaTags = ['salary', 'general', 'share'];
    } else if (rentLike >= 1 && bigTicket >= 2) {
        persona = 'The Big-Ticket Realist';
        personaTags = ['salary', 'general', 'share'];
    } else if (fuelCount >= 4) {
        persona = 'The Petrol Pump Regular';
        personaTags = ['metro', 'general', 'share'];
    } else if (qcCount + foodCount >= 10) {
        persona = 'The Urban Impulse';
        personaTags = ['food', 'qcommerce', 'share'];
    } else if (mid.length >= 25 && micro.length < 15) {
        persona = 'The Mid-Range Tapper';
        personaTags = ['general', 'share'];
    } else if (avg >= 800 && debits.length >= 8 && micro.length < 10) {
        persona = 'The Fewer-Bigger Spender';
        personaTags = ['general', 'salary', 'share'];
    } else if (debits.length >= 60 && totalSpent / Math.max(1, debits.length) < 250) {
        persona = 'The High-Frequency Tapper';
        personaTags = ['micro', 'general', 'share'];
    } else if (debits.length <= 12 && totalSpent > 0) {
        persona = 'The Selective Scanner';
        personaTags = ['general', 'share'];
    }

    // Rough "estimated" = ~42% of actual (perception gap storytelling)
    const estimated = Math.round(totalSpent * 0.42);

    let flexLine = `Just audited my UPI offline on Spenderly.in — ${formatINR(totalSpent)} across ${debits.length} taps`;
    if (qcCount >= 3 && foodCount >= 1) {
        flexLine = `Just realized I spent more on Blinkit/Zepto this month than I thought. Checked it offline on Spenderly.in`;
    } else if (micro.length >= 20) {
        flexLine = `${micro.length} UPI taps under ₹100 added up silently. Found it offline on Spenderly.in`;
    } else if (lateFood.length >= 4) {
        flexLine = `Too many food orders after 10 PM showed up in my UPI audit on Spenderly.in`;
    } else if (p2p.length >= 10) {
        flexLine = `People vs shops on UPI — audited offline on Spenderly.in`;
    }

    const tagline = pickSnark(`persona:${persona}:${debits.length}`, personaTags);

    return {
        persona,
        tagline,
        topFoodApp: topFood ? { name: topFood[0], orders: topFood[1] } : null,
        microTaps: micro.length,
        estimatedSpend: estimated,
        actualSpend: Math.round(totalSpent),
        tapCount: debits.length,
        flexLine,
    };
}
